import React, { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { ContextSession } from "../../services/ContextSession";
import logoInjupemp from "../../img/LogoInjupemp2026.png";
import {
  obtenerReporteProduccionResumenEnrolamiento,
} from "./enrolamientoApi";
import {
  formatIsoDateToEs,
  getCurrentMonthDateRange,
  isSinOficinaReporte,
  openHtmlPrintWindow,
  resolveReporteOfficeName,
  safeText,
  validateReportDateRange,
} from "./reporteriaUtils";

const buildPrintHtml = (payload) => {
  const filtros = payload?.filtros || {};
  const items = (Array.isArray(payload?.items) ? payload.items : []).filter(
    (item) =>
      !isSinOficinaReporte({
        oficinaNombre: item?.OficinaNombre,
        oficinaOrigen: item?.OficinaOrigen,
        oficinaId: item?.OficinaId,
        oficinaKey: item?.OficinaKey,
      }),
  );
  const totalRegistrosVisibles = items.reduce(
    (acc, item) => acc + Number(item?.Total || 0),
    0,
  );
  const rows = items
    .map((item) => {
      const oficinaNombre = resolveReporteOfficeName({
        oficinaNombre: item?.OficinaNombre,
        oficinaOrigen: item?.OficinaOrigen,
        oficinaId: item?.OficinaId,
        oficinaKey: item?.OficinaKey,
      });
      return `
      <tr>
        <td>${safeText(oficinaNombre)} (${safeText(item?.OficinaOrigen)})</td>
        <td style="text-align:right">${Number(item?.Total || 0)}</td>
      </tr>
    `;
    })
    .join("");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Reporte de Producción Resumen - Enrolamiento</title>
    <style>
      body { font-family: Arial, sans-serif; color: #111; margin: 20px; font-size: 12px; }
      .actions { text-align: right; margin-bottom: 10px; }
      .btn { border: 0; border-radius: 4px; background: #19498c; color: #fff; padding: 8px 14px; cursor: pointer; }
      .header { display: flex; align-items: center; gap: 18px; margin-bottom: 6px; }
      .logo { height: 60px; width: auto; }
      .title { font-size: 22px; font-weight: 700; margin: 0; }
      .date-row { display: flex; gap: 40px; margin-bottom: 12px; font-size: 18px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      th, td { border: 1px solid #222; padding: 6px 8px; font-size: 13px; text-align: left; }
      th { background: #f1f4f9; }
      .total { font-size: 20px; font-weight: 700; text-align: right; margin-top: 10px; }
      @media print { .actions { display: none; } }
    </style>
  </head>
  <body>
    <div class="actions">
      <button class="btn" onclick="window.print()">Imprimir</button>
    </div>
    <div class="header">
      <img class="logo" src="${logoInjupemp}" alt="INJUPEMP" />
      <h1 class="title">Reporte de Producción Resumen</h1>
    </div>
    <div class="date-row">
      <div><b>Fecha Inicial:</b> ${formatIsoDateToEs(filtros?.fecha_inicio)}</div>
      <div><b>Fecha Final:</b> ${formatIsoDateToEs(filtros?.fecha_fin)}</div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Oficina</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <p class="total">Total de Personas Registradas: ${totalRegistrosVisibles}</p>
  </body>
</html>`;
};

const buildExcelRows = (payload) => {
  const items = (Array.isArray(payload?.items) ? payload.items : []).filter(
    (item) =>
      !isSinOficinaReporte({
        oficinaNombre: item?.OficinaNombre,
        oficinaOrigen: item?.OficinaOrigen,
        oficinaId: item?.OficinaId,
        oficinaKey: item?.OficinaKey,
      }),
  );
  return items.map((item) => {
    const oficinaNombre = resolveReporteOfficeName({
      oficinaNombre: item?.OficinaNombre,
      oficinaOrigen: item?.OficinaOrigen,
      oficinaId: item?.OficinaId,
      oficinaKey: item?.OficinaKey,
    });
    return {
      Oficina: safeText(oficinaNombre),
      Origen: safeText(item?.OficinaOrigen),
      Total: Number(item?.Total || 0),
    };
  });
};

const toSafeFileDate = (value) => {
  const raw = safeText(value) || "sin-fecha";
  return raw.replace(/[^\dA-Za-z-]/g, "-");
};

export default function ReporteProduccionResumenEnrolamiento() {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";

  const currentMonthRange = getCurrentMonthDateRange();
  const [fechaInicio, setFechaInicio] = useState(currentMonthRange.fechaInicio);
  const [fechaFin, setFechaFin] = useState(currentMonthRange.fechaFin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reporte, setReporte] = useState(null);

  const handleConsultar = async () => {
    const validationError = validateReportDateRange(fechaInicio, fechaFin);
    if (validationError) {
      setError(validationError);
      setReporte(null);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await obtenerReporteProduccionResumenEnrolamiento(authToken, {
        fechaInicio,
        fechaFin,
      });
      setReporte(data || null);
    } catch (err) {
      setError(err?.message || "No se pudo generar el reporte resumen.");
      setReporte(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImprimir = () => {
    if (!reporte) return;
    openHtmlPrintWindow(buildPrintHtml(reporte));
  };

  const handleExportarExcel = () => {
    if (!reporte) return;
    const rows = buildExcelRows(reporte);
    if (!rows.length) return;

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 35 },
      { wch: 14 },
      { wch: 10 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "ProduccionResumen");

    const fechaInicio = toSafeFileDate(reporte?.filtros?.fecha_inicio);
    const fechaFin = toSafeFileDate(reporte?.filtros?.fecha_fin);
    XLSX.writeFile(workbook, `reporte_produccion_resumen_${fechaInicio}_a_${fechaFin}.xlsx`);
  };

  const items = (Array.isArray(reporte?.items) ? reporte.items : []).filter(
    (item) =>
      !isSinOficinaReporte({
        oficinaNombre: item?.OficinaNombre,
        oficinaOrigen: item?.OficinaOrigen,
        oficinaId: item?.OficinaId,
        oficinaKey: item?.OficinaKey,
      }),
  );
  const totalRegistrosVisibles = items.reduce(
    (acc, item) => acc + Number(item?.Total || 0),
    0,
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dce7f7" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#19498c", mb: 2 }}>
          Reporte de Producción Resumen
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Fecha inicio"
            type="date"
            size="small"
            value={fechaInicio}
            onChange={(event) => setFechaInicio(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: fechaFin }}
          />
          <TextField
            label="Fecha final"
            type="date"
            size="small"
            value={fechaFin}
            onChange={(event) => setFechaFin(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fechaInicio }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleConsultar}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#19498c" }}
          >
            {loading ? "Consultando..." : "Consultar"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleImprimir}
            disabled={!reporte}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Imprimir
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportarExcel}
            disabled={!reporte || !items.length}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Excel
          </Button>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {reporte ? (
          <>
            <Typography sx={{ color: "#4f5f77", fontSize: 13, mb: 2 }}>
              Período: {formatIsoDateToEs(reporte?.filtros?.fecha_inicio)} a {formatIsoDateToEs(reporte?.filtros?.fecha_fin)}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ border: "1px solid #c5cedd", padding: 7, textAlign: "left" }}>Oficina</th>
                    <th style={{ border: "1px solid #c5cedd", padding: 7, textAlign: "right" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const oficinaNombre = resolveReporteOfficeName({
                      oficinaNombre: item?.OficinaNombre,
                      oficinaOrigen: item?.OficinaOrigen,
                      oficinaId: item?.OficinaId,
                      oficinaKey: item?.OficinaKey,
                    });
                    return (
                      <tr key={item?.OficinaKey}>
                        <td style={{ border: "1px solid #e0e5ef", padding: 7 }}>
                          {oficinaNombre} ({item?.OficinaOrigen})
                        </td>
                        <td style={{ border: "1px solid #e0e5ef", padding: 7, textAlign: "right" }}>{Number(item?.Total || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
            <Typography sx={{ textAlign: "right", fontWeight: 700, mt: 1, fontSize: 20 }}>
              Total de Personas Registradas: {totalRegistrosVisibles}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: "#5a6a80" }}>
            Seleccione la fecha inicial y la fecha final para generar el reporte.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
